// ai-sidebar/use-assistant.tsx
// State + orchestration for the Page Assistant sidebar.
//
// Holds the conversation, drives the real SSE stream (no fake streaming), and
// owns the lazy setup: ensure the hidden Page Assistant agent exists, find or
// create the per-user-per-brand thread, and re-point page context on navigation.
//
// State lives in a useReducer; everything below composes it. No Redux/Zustand.

import React, {
  createContext, useContext, useReducer, useRef, useCallback, useEffect, useMemo,
} from "react";
import {
  upmodeAgentApi,
  streamChat,
  type ChatStreamEvent,
  type UpmodeConversationSummary,
} from "@/app/services/upmode-agent/upmode-agent-api";
import type { AssistantStatus, Phase, UIMessage } from "./types";
import { pageLabel } from "./suggestions";

// ── Local id generator (stable within a session) ────────────────────────────
let _seq = 0;
const nextId = () => `m${++_seq}`;

// ── State ────────────────────────────────────────────────────────────────────
interface State {
  collapsed: boolean;
  status: AssistantStatus;
  messages: UIMessage[];
  currentPage: Phase;
  brandName: string | null;
  agentId: number | null;
  conversationId: number | null;
  recent: UpmodeConversationSummary[];
  /** Id of the assistant message currently streaming, if any. */
  streamingId: string | null;
  errorText: string | null;
  initialized: boolean;
}

type Action =
  | { t: "SET_COLLAPSED"; v: boolean }
  | { t: "SET_PAGE"; v: Phase }
  | { t: "INIT"; agentId: number; conversationId: number | null; recent: UpmodeConversationSummary[] }
  | { t: "SET_RECENT"; recent: UpmodeConversationSummary[] }
  | { t: "SET_CONVERSATION"; id: number }
  | { t: "LOAD_THREAD"; id: number; messages: UIMessage[] }
  | { t: "SEND"; userId: string; assistantId: string; text: string }
  | { t: "DELTA"; id: string; delta: string }
  | { t: "TOOL_START"; msgId: string; toolId: string; name: string }
  | { t: "TOOL_DONE"; msgId: string; toolId: string; name: string; meta?: string; failed?: boolean; result?: unknown }
  | { t: "DONE"; id: string }
  | { t: "ERROR"; id: string | null; message: string }
  | { t: "RESET_THREAD" };

function mapMsg(messages: UIMessage[], id: string, fn: (m: UIMessage) => UIMessage): UIMessage[] {
  return messages.map((m) => (m.id === id ? fn(m) : m));
}

function reducer(state: State, a: Action): State {
  switch (a.t) {
    case "SET_COLLAPSED":
      return { ...state, collapsed: a.v };
    case "SET_PAGE":
      return { ...state, currentPage: a.v };
    case "INIT":
      return {
        ...state,
        agentId: a.agentId,
        conversationId: a.conversationId,
        recent: a.recent,
        initialized: true,
      };
    case "SET_RECENT":
      return { ...state, recent: a.recent };
    case "SET_CONVERSATION":
      return { ...state, conversationId: a.id };
    case "LOAD_THREAD":
      return {
        ...state,
        conversationId: a.id,
        messages: a.messages,
        status: "idle",
        streamingId: null,
        errorText: null,
      };
    case "SEND":
      return {
        ...state,
        status: "thinking",
        errorText: null,
        streamingId: a.assistantId,
        messages: [
          ...state.messages,
          { id: a.userId, role: "user", content: a.text, complete: true },
          { id: a.assistantId, role: "assistant", content: "", streaming: true, tools: [] },
        ],
      };
    case "DELTA":
      return {
        ...state,
        status: "streaming",
        messages: mapMsg(state.messages, a.id, (m) => ({ ...m, content: m.content + a.delta, streaming: true })),
      };
    case "TOOL_START":
      return {
        ...state,
        messages: mapMsg(state.messages, a.msgId, (m) => ({
          ...m,
          tools: [...(m.tools ?? []), { id: a.toolId, name: a.name, state: "running" }],
        })),
      };
    case "TOOL_DONE":
      return {
        ...state,
        messages: mapMsg(state.messages, a.msgId, (m) => ({
          ...m,
          tools: (m.tools ?? []).map((tl) =>
            tl.id === a.toolId
              ? { ...tl, state: a.failed ? "failed" : "done", meta: a.meta, result: a.result }
              : tl,
          ),
        })),
      };
    case "DONE":
      return {
        ...state,
        status: "idle",
        streamingId: null,
        messages: mapMsg(state.messages, a.id, (m) => ({ ...m, streaming: false, complete: true })),
      };
    case "ERROR":
      return {
        ...state,
        status: "error",
        streamingId: null,
        errorText: a.message,
        messages: a.id
          ? mapMsg(state.messages, a.id, (m) => ({ ...m, streaming: false, complete: true, error: true }))
          : state.messages,
      };
    case "RESET_THREAD":
      return { ...state, messages: [], conversationId: null, status: "idle", streamingId: null, errorText: null };
    default:
      return state;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function resolveBrand(explicit?: string | null): string | null {
  if (explicit) return explicit;
  try {
    return localStorage.getItem("bm_brand") || null;
  } catch {
    return null;
  }
}

/** Short, calm meta for a finished tool step, derived defensively from result. */
function toolMeta(result: unknown): string | undefined {
  try {
    if (Array.isArray(result)) return `${result.length} found`;
    if (result && typeof result === "object") {
      const r = result as Record<string, unknown>;
      if (Array.isArray(r.items)) return `${(r.items as unknown[]).length} found`;
      if (Array.isArray(r.results)) return `${(r.results as unknown[]).length} found`;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

// ── Context ────────────────────────────────────────────────────────────────────
interface AssistantApi extends State {
  toggleCollapsed: () => void;
  setCollapsed: (v: boolean) => void;
  send: (text: string) => Promise<void>;
  stop: () => void;
  resetThread: () => void;
  resumeConversation: (id: number) => Promise<void>;
}

const Ctx = createContext<AssistantApi | null>(null);

export function useAssistant(): AssistantApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAssistant must be used inside <AssistantProvider>");
  return v;
}

// ── Provider ───────────────────────────────────────────────────────────────────
export function AssistantProvider({
  phase,
  brandName,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  onCollapsedChange,
  children,
}: {
  phase: Phase;
  brandName?: string | null;
  defaultCollapsed?: boolean;
  /** Controlled collapse (mount sites pass `isCollapsed`). Omit for uncontrolled. */
  collapsed?: boolean;
  onCollapsedChange?: (v: boolean) => void;
  children: React.ReactNode;
}) {
  const brand = resolveBrand(brandName);
  const controlled = collapsedProp !== undefined;

  const [state, dispatch] = useReducer(reducer, {
    collapsed: defaultCollapsed,
    status: "idle",
    messages: [],
    currentPage: phase,
    brandName: brand,
    agentId: null,
    conversationId: null,
    recent: [],
    streamingId: null,
    errorText: null,
    initialized: false,
  });

  const abortRef = useRef<AbortController | null>(null);
  const initRef = useRef<Promise<{ agentId: number; conversationId: number | null }> | null>(null);

  // Keep currentPage in sync with the host page.
  useEffect(() => {
    dispatch({ t: "SET_PAGE", v: phase });
  }, [phase]);

  /**
   * Lazy setup, run once: ensure the hidden Page Assistant agent exists, then
   * find the existing per-brand thread (do NOT create one yet — empty threads
   * are only created on first send). Cheap: no LLM call here.
   */
  const ensureReady = useCallback((): Promise<{ agentId: number; conversationId: number | null }> => {
    if (initRef.current) return initRef.current;
    const p = (async () => {
      const agent = await upmodeAgentApi.ensurePageAssistant();
      let convos: UpmodeConversationSummary[] = [];
      try {
        convos = await upmodeAgentApi.listConversations(100);
      } catch {
        convos = [];
      }
      // Discrete conversations: Recent lists this brand's past Page Assistant
      // chats that have a real title. We do NOT reuse one giant per-brand thread;
      // each open starts fresh, so a new question begins its own conversation.
      const recent = convos
        .filter((c) => c.agent_id === agent.id)
        .filter((c) => (c.brand_name || "").toLowerCase() === (brand || "").toLowerCase())
        .filter((c) => !!c.title && c.title.trim() !== "" && c.title !== "New conversation")
        .sort((x, y) => (y.updated_at || "").localeCompare(x.updated_at || ""))
        .slice(0, 8);
      dispatch({ t: "INIT", agentId: agent.id, conversationId: null, recent });
      return { agentId: agent.id, conversationId: null };
    })();
    initRef.current = p;
    // Never cache a failed setup — clear it so the next click retries instead
    // of replaying the same rejection forever.
    p.catch(() => {
      if (initRef.current === p) initRef.current = null;
    });
    return p;
  }, [brand]);

  useEffect(() => {
    // Warm up so the launchpad can show recent threads. Safe + cheap.
    ensureReady().catch(() => {/* surfaced on send */});
  }, [ensureReady]);

  const effectiveCollapsed = controlled ? !!collapsedProp : state.collapsed;
  const setCollapsed = useCallback(
    (v: boolean) => {
      if (controlled) onCollapsedChange?.(v);
      else dispatch({ t: "SET_COLLAPSED", v });
    },
    [controlled, onCollapsedChange],
  );
  const toggleCollapsed = useCallback(() => setCollapsed(!effectiveCollapsed), [setCollapsed, effectiveCollapsed]);
  const resetThread = useCallback(() => dispatch({ t: "RESET_THREAD" }), []);

  const send = useCallback(
    async (text: string) => {
      const clean = text.trim();
      if (!clean || state.status === "thinking" || state.status === "streaming") return;

      // Show the user's message + thinking state immediately, before any network
      // work. Every click gives visible feedback even if setup later fails.
      const userId = nextId();
      const assistantId = nextId();
      dispatch({ t: "SEND", userId, assistantId, text: clean });

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        // Lazy setup (idempotent): ensure the hidden Page Assistant agent and a
        // per-brand thread exist. Failures here surface as the error card below.
        const { agentId, conversationId } = await ensureReady();
        let convId = conversationId ?? state.conversationId;
        if (!convId) {
          const conv = await upmodeAgentApi.createConversation({
            agent_id: agentId,
            brand_name: brand,
            title: "New conversation",
          });
          convId = conv.id;
          dispatch({ t: "SET_CONVERSATION", id: conv.id });
          dispatch({ t: "SET_RECENT", recent: [conv, ...state.recent].slice(0, 5) });
        }

        await streamChat(
          convId,
          clean,
          (evt: ChatStreamEvent) => {
            switch (evt.type) {
              case "text":
                dispatch({ t: "DELTA", id: assistantId, delta: evt.delta });
                break;
              case "tool_use_start":
                dispatch({ t: "TOOL_START", msgId: assistantId, toolId: evt.id, name: evt.name });
                break;
              case "tool_result":
                dispatch({
                  t: "TOOL_DONE",
                  msgId: assistantId,
                  toolId: evt.id,
                  name: evt.name,
                  meta: toolMeta(evt.result),
                  result: evt.result,
                });
                break;
              case "error":
                // Backend stream errors can carry raw internals; log the
                // detail and show a generic message.
                console.error("Assistant stream error:", evt.message);
                dispatch({ t: "ERROR", id: assistantId, message: "Something interrupted the response. Please try again." });
                break;
              // email_confirmation / publish_confirmation: the Page Assistant
              // has neither capability enabled, so these are not expected here.
              // done: finalized when the stream promise resolves, below.
              default:
                break;
            }
          },
          controller.signal,
          { page: state.currentPage, page_label: pageLabel(state.currentPage) },
        );
        dispatch({ t: "DONE", id: assistantId });
      } catch (e: any) {
        if (e?.name === "AbortError") {
          // User stopped generation — keep the partial answer, no error state.
          dispatch({ t: "DONE", id: assistantId });
        } else {
          console.error("Assistant request failed:", e);
          dispatch({ t: "ERROR", id: assistantId, message: e?.message || "Something interrupted the response." });
        }
      } finally {
        abortRef.current = null;
      }
    },
    [brand, ensureReady, state.status, state.conversationId, state.recent, state.currentPage],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const resumeConversation = useCallback(async (id: number) => {
    try {
      const detail = await upmodeAgentApi.getConversation(id);
      const loaded: UIMessage[] = detail.messages
        .filter((m) => (m.role === "user" || m.role === "assistant") && (m.content ?? "").trim() !== "")
        .map((m) => ({
          id: nextId(),
          role: m.role as "user" | "assistant",
          content: m.content,
          complete: true,
        }));
      dispatch({ t: "LOAD_THREAD", id, messages: loaded });
    } catch (e: any) {
      dispatch({ t: "ERROR", id: null, message: e?.message || "Could not load that conversation." });
    }
  }, []);

  const value = useMemo<AssistantApi>(
    () => ({
      ...state,
      collapsed: effectiveCollapsed,
      toggleCollapsed,
      setCollapsed,
      send,
      stop,
      resetThread,
      resumeConversation,
    }),
    [state, effectiveCollapsed, toggleCollapsed, setCollapsed, send, stop, resetThread, resumeConversation],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
